import { useMemo, useState, type MouseEvent, type ReactNode } from "react";
import type {
  Colecao,
  ModoSincronizacaoReferencias,
  OrdenacaoDaGaleria,
  Referencia,
} from "@base/contracts";
import {
  BarChart3,
  Download,
  ExternalLink,
  Images,
  Instagram,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  Trash2,
  X,
} from "lucide-react";
import {
  Esqueleto,
  EstadoDeErro,
  EstadoVazio,
  MolduraDoApp,
} from "@base/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageLayout } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Combobox } from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toggle } from "@/components/ui/toggle";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import { ErroDaApi } from "../../../shared/http/erro-da-api";
import { useAuth } from "../../../shared/auth/auth.contexto";
import { useDialogos } from "../../../shared/ui/dialogos.contexto";
import { baixarMidiasDaReferencia } from "./baixar-midias-da-referencia";
import {
  useAtualizarBiblioteca,
  useAtualizarFlagsPost,
  useListarColecoes,
  useListarReferenciasInfinito,
  useSincronizar,
  useStatusSessaoInstagram,
  useStatusSincronizacao,
} from "../application/use-referencias";
import { useNavegacaoDoApp } from "./navegacao-do-app";
import { MidiaAutenticada } from "./midia-autenticada";
import { ModalDeReferencia } from "./modal-de-referencia";
import { rodapeDaSessaoNaLateral } from "./rodape-da-sessao";
import {
  OPCOES_DE_FILTRO_ANUNCIO,
  OPCOES_DE_ORDENACAO_REFERENCIAS,
  type FiltroDeAnuncio,
} from "./seletor-de-ordenacao";
import { usePropsDoSeletorNaMoldura } from "../../../shared/tenant/projeto.contexto";
import { LogoRenkeRefs } from "../../../shared/branding/logo-renke-refs";

/**
 * Galeria image-first. Clique abre modal; sync traz tudo do Instagram + midia local.
 */
export function PaginaDeReferencias() {
  const [busca, definirBusca] = useState("");
  const [collectionId, definirCollectionId] = useState<string | undefined>(
    undefined,
  );
  const [aberta, definirAberta] = useState<Referencia | null>(null);
  const [ordenar, definirOrdenar] = useState<OrdenacaoDaGaleria>("recente");
  const [filtroAnuncio, definirFiltroAnuncio] =
    useState<FiltroDeAnuncio>("todos");
  const [soFavoritos, definirSoFavoritos] = useState(false);
  const [popupSyncAberto, definirPopupSyncAberto] = useState(false);

  const termo = busca.trim();
  const filtrosAtivos =
    termo.length > 0 ||
    collectionId !== undefined ||
    filtroAnuncio !== "todos" ||
    soFavoritos ||
    ordenar !== "recente";
  const isAd =
    filtroAnuncio === "anuncio"
      ? true
      : filtroAnuncio === "organico"
        ? false
        : undefined;
  const filtro = {
    ...(collectionId !== undefined ? { collectionId } : {}),
    ...(termo.length > 0 ? { q: termo } : {}),
    ordenar,
    ...(isAd !== undefined ? { isAd } : {}),
    ...(soFavoritos ? { soFavoritos: true } : {}),
  };

  const lista = useListarReferenciasInfinito(filtro);
  const colecoes = useListarColecoes();
  const sincronizacao = useStatusSincronizacao();
  const sincronizar = useSincronizar();
  const atualizarBiblioteca = useAtualizarBiblioteca();
  const sessaoIg = useStatusSessaoInstagram();

  const rodando =
    sincronizacao.data?.running === true || sincronizar.isPending;
  const breakerAte = sessaoIg.data?.breakerAte ?? null;
  const emPausaSeguranca = Boolean(breakerAte);

  const referencias = useMemo(
    () => lista.data?.pages.flatMap((pagina) => pagina.dados) ?? [],
    [lista.data],
  );
  const total = referencias.length;
  const aindaCarregando =
    lista.isFetchingNextPage ||
    (lista.hasNextPage === true && !lista.isError);
  const statusSync = sincronizacao.data?.status ?? "idle";
  const mensagemSync = sincronizacao.data?.message;

  const colecaoAtiva = useMemo(
    () => colecoes.data?.find((c) => c.id === collectionId),
    [colecoes.data, collectionId],
  );

  const rotuloSync = useMemo(() => {
    if (emPausaSeguranca) return "Pausa por segurança";
    if (rodando) return "Sincronizando…";
    if (statusSync === "success") return "Atualizado";
    if (statusSync === "error") return "Falhou";
    return "Pronto";
  }, [emPausaSeguranca, rodando, statusSync]);

  const seletorProjeto = usePropsDoSeletorNaMoldura();
  const navegacao = useNavegacaoDoApp("referencias");

  const iniciarSync = (modo: ModoSincronizacaoReferencias) => {
    definirPopupSyncAberto(false);
    sincronizar.mutate(modo);
  };

  return (
    <MolduraDoApp
      nomeDoApp="Creative OS"
      marca={<LogoRenkeRefs />}
      titulo="Referências"
      subtitulo="Biblioteca visual da conta"
      navegacao={navegacao}
      rodapeDaLateral={rodapeDaSessaoNaLateral}
      {...seletorProjeto}
      acoes={
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant={
              emPausaSeguranca || statusSync === "error"
                ? "destructive"
                : statusSync === "success"
                  ? "secondary"
                  : "outline"
            }
          >
            {rotuloSync}
          </Badge>
          <Button
            variant="outline"
            onClick={() => atualizarBiblioteca.mutate()}
            loading={atualizarBiblioteca.isPending}
            aria-label="Atualizar biblioteca"
            title="Recarrega a lista do banco sem iniciar um novo sync"
          >
            <RotateCcw className="size-4" aria-hidden="true" />
            Atualizar
          </Button>
          <Popover open={popupSyncAberto} onOpenChange={definirPopupSyncAberto}>
            <PopoverTrigger asChild>
              <Button
                disabled={rodando || emPausaSeguranca}
                loading={rodando}
                aria-label="Sincronizar Instagram"
                title={
                  emPausaSeguranca && breakerAte
                    ? `Instagram pediu pausa; tente depois de ${new Date(breakerAte).toLocaleString("pt-BR")}`
                    : "Escolha se quer só o novo ou a atualização completa"
                }
              >
                <RefreshCw className="size-4" aria-hidden="true" />
                {rodando ? "Sincronizando…" : "Sincronizar"}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[min(100vw-2rem,22rem)] space-y-3"
            >
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Como sincronizar?
                </p>
                <p className="text-sm text-muted-foreground">
                  Escolha o tipo de sync. Os dois usam a sessão do Instagram
                  desta conta.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <BotaoOpcaoSync
                  icone={<Sparkles className="size-5" aria-hidden="true" />}
                  titulo="Conteúdos novos"
                  tag="Mais rápido"
                  descricao="Só o que ainda não está na plataforma."
                  onClick={() => iniciarSync("novos")}
                />
                <BotaoOpcaoSync
                  icone={<BarChart3 className="size-5" aria-hidden="true" />}
                  titulo="Atualizar resultados"
                  tag="Mais demorado"
                  descricao="Atualiza métricas e dados dos posts que já existem."
                  onClick={() => iniciarSync("completo")}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      }
    >
      <PageLayout className="animate-aparecer">
        {emPausaSeguranca && breakerAte ? (
          <p className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            Instagram pediu pausa. Sync desligado até{" "}
            {new Date(breakerAte).toLocaleString("pt-BR")}.
          </p>
        ) : null}
        <div className="space-y-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                type="search"
                value={busca}
                onChange={(evento) => definirBusca(evento.target.value)}
                placeholder="Buscar por perfil ou legenda"
                aria-label="Buscar referências"
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <ToggleGroup
                type="single"
                value={filtroAnuncio}
                onValueChange={(valor) => {
                  if (
                    valor === "todos" ||
                    valor === "anuncio" ||
                    valor === "organico"
                  ) {
                    definirFiltroAnuncio(valor);
                  }
                }}
                variant="outline"
                size="sm"
                className="justify-start"
                aria-label="Filtrar anúncio ou orgânico"
              >
                {OPCOES_DE_FILTRO_ANUNCIO.map((opcao) => (
                  <ToggleGroupItem
                    key={opcao.valor}
                    value={opcao.valor}
                    className="cursor-pointer px-3"
                  >
                    {opcao.valor === "todos" ? "Tudo" : opcao.rotulo}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              <Toggle
                pressed={soFavoritos}
                onPressedChange={definirSoFavoritos}
                variant="outline"
                size="sm"
                className="cursor-pointer px-3"
                aria-label="Só favoritos"
              >
                <Star
                  className="size-4"
                  aria-hidden
                  fill={soFavoritos ? "currentColor" : "none"}
                />
                Favoritos
              </Toggle>
              <Combobox
                className="w-[14rem]"
                value={collectionId ?? "todas"}
                placeholder="Todas as coleções"
                searchPlaceholder="Buscar coleção"
                vazio="Nenhuma coleção com esse nome."
                options={[
                  { value: "todas", label: "Todas as coleções" },
                  ...(colecoes.data ?? []).map((colecao) => ({
                    value: colecao.id,
                    label:
                      colecao.mediaCount !== null
                        ? `${colecao.nome} (${colecao.mediaCount})`
                        : colecao.nome,
                  })),
                ]}
                onChange={(valor) =>
                  definirCollectionId(valor === "todas" ? undefined : valor)
                }
              />
              <Select
                value={ordenar}
                onValueChange={(valor) => {
                  const opcao = OPCOES_DE_ORDENACAO_REFERENCIAS.find(
                    (item) => item.valor === valor,
                  );
                  if (opcao) definirOrdenar(opcao.valor);
                }}
              >
                <SelectTrigger className="w-[12rem]" aria-label="Ordenar">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  {OPCOES_DE_ORDENACAO_REFERENCIAS.map((opcao) => (
                    <SelectItem key={opcao.valor} value={opcao.valor}>
                      {opcao.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              {lista.isPending && total === 0
                ? "Carregando a biblioteca…"
                : resumirContagem({
                    total,
                    aindaCarregando,
                    colecao: colecaoAtiva,
                    totalColecoes: colecoes.data?.length,
                  })}
            </p>
            {filtrosAtivos ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="cursor-pointer"
                onClick={() => {
                  definirBusca("");
                  definirCollectionId(undefined);
                  definirFiltroAnuncio("todos");
                  definirSoFavoritos(false);
                  definirOrdenar("recente");
                }}
              >
                <X className="size-4" aria-hidden />
                Limpar filtros
              </Button>
            ) : null}
          </div>
          {mensagemSync && statusSync !== "idle" && !rodando ? (
            <p className="text-sm text-muted-foreground" role="status">
              {mensagemSync}
            </p>
          ) : null}
        </div>

        {lista.isPending && total === 0 ? <GradeDeEsqueleto /> : null}

        {lista.isError ? (
          <EstadoDeErro
            mensagem={mensagemDoErro(lista.error)}
            {...(requestIdDoErro(lista.error)
              ? { codigo: requestIdDoErro(lista.error) as string }
              : {})}
            aoTentarNovamente={() => void lista.refetch()}
          />
        ) : null}

        {!lista.isPending && !lista.isError && total === 0 ? (
          <EstadoVazio
            icone={<Instagram />}
            titulo="Nenhuma referência ainda"
            descricao="Sincronize o Instagram ou ajuste a busca e o filtro de coleção."
          />
        ) : null}

        {total > 0 ? (
          <div className="space-y-4">
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {referencias.map((referencia) => (
                <li key={referencia.id}>
                  <TileDeReferencia
                    referencia={referencia}
                    aoAbrir={() => definirAberta(referencia)}
                  />
                </li>
              ))}
            </ul>
            {aindaCarregando ? (
              <p
                className="text-center text-sm text-muted-foreground"
                role="status"
              >
                Carregando o restante…
              </p>
            ) : null}
          </div>
        ) : null}
      </PageLayout>

      {aberta ? (
        <ModalDeReferencia
          referencia={aberta}
          aoFechar={() => definirAberta(null)}
        />
      ) : null}
    </MolduraDoApp>
  );
}

function resumirContagem({
  total,
  aindaCarregando,
  colecao,
  totalColecoes,
}: {
  readonly total: number;
  readonly aindaCarregando: boolean;
  readonly colecao: Colecao | undefined;
  readonly totalColecoes: number | undefined;
}): string {
  const base =
    total === 0
      ? "Nenhuma referência sincronizada"
      : `${total} referência${total === 1 ? "" : "s"}${aindaCarregando ? "…" : ""}`;

  if (colecao?.mediaCount !== null && colecao?.mediaCount !== undefined) {
    if (aindaCarregando) {
      return `${base} · ${colecao.mediaCount} no Instagram`;
    }
    if (total < colecao.mediaCount) {
      return `${base} de ${colecao.mediaCount} no Instagram. Rode Sincronizar de novo`;
    }
    return `${base} · coleção completa`;
  }

  if (totalColecoes !== undefined && colecao === undefined) {
    return `${base} · ${totalColecoes} coleç${totalColecoes === 1 ? "ão" : "ões"}`;
  }

  return base;
}

function BotaoOpcaoSync({
  icone,
  titulo,
  tag,
  descricao,
  onClick,
}: {
  readonly icone: ReactNode;
  readonly titulo: string;
  readonly tag: string;
  readonly descricao: string;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="mt-0.5 text-foreground">{icone}</span>
      <span className="min-w-0 space-y-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{titulo}</span>
          <Badge variant="secondary">{tag}</Badge>
        </span>
        <span className="block text-sm leading-relaxed text-muted-foreground">
          {descricao}
        </span>
      </span>
    </button>
  );
}

function GradeDeEsqueleto() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Carregando referências"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      <span className="sr-only">Carregando referências</span>
      {Array.from({ length: 8 }).map((_, indice) => (
        <div
          key={indice}
          className="overflow-hidden rounded-xl border border-border/60 bg-card"
        >
          <Esqueleto className="aspect-square rounded-none" />
          <div className="space-y-2 p-3">
            <Esqueleto className="h-3.5 w-1/2" />
            <Esqueleto className="h-3 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

function TileDeReferencia({
  referencia,
  aoAbrir,
}: {
  readonly referencia: Referencia;
  readonly aoAbrir: () => void;
}) {
  const { tokenAtual } = useAuth();
  const { avisar, confirmar } = useDialogos();
  const flags = useAtualizarFlagsPost();
  const [baixando, definirBaixando] = useState(false);
  const username = referencia.username
    ? `@${referencia.username}`
    : "Sem usuário";
  const caption = referencia.caption?.trim() || "Sem legenda";
  const rotuloTipo = rotuloDoTipoDeMedia(referencia.mediaType);
  const temMidia = Boolean(
    referencia.imageUrl ||
      referencia.videoUrl ||
      referencia.images.length > 0,
  );

  const aoBaixar = async (evento: MouseEvent<HTMLButtonElement>) => {
    evento.preventDefault();
    evento.stopPropagation();
    if (!temMidia || baixando) return;
    definirBaixando(true);
    try {
      const qtd = await baixarMidiasDaReferencia(referencia, tokenAtual());
      if (qtd === 0) {
        await avisar({
          titulo: "Nada para baixar",
          descricao: "Esta referência ainda não tem mídia salva.",
        });
      }
    } catch {
      await avisar({
        titulo: "Não deu para baixar",
        descricao: "Tente de novo em instantes.",
      });
    } finally {
      definirBaixando(false);
    }
  };

  const aoFavoritar = (evento: MouseEvent<HTMLButtonElement>) => {
    evento.preventDefault();
    evento.stopPropagation();
    flags.mutate({ id: referencia.id, favorito: !referencia.favorito });
  };

  const aoExcluir = async (evento: MouseEvent<HTMLButtonElement>) => {
    evento.preventDefault();
    evento.stopPropagation();
    const ok = await confirmar({
      titulo: "Excluir desta biblioteca?",
      descricao:
        "Some só daqui. O post continua no Instagram e nas suas coleções.",
      rotuloConfirmar: "Excluir",
    });
    if (!ok) return;
    flags.mutate({ id: referencia.id, oculto: true });
  };

  return (
    <article className="group relative h-full overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-border hover:shadow-md">
      <div className="absolute left-2 top-2 z-[10] flex flex-col gap-1.5 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100">
        {temMidia ? (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-8 rounded-md bg-background/95 shadow-xs hover:bg-background"
            aria-label="Baixar mídia"
            title="Baixar mídia"
            disabled={baixando}
            onClick={(evento) => {
              void aoBaixar(evento);
            }}
          >
            <Download className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="size-8 rounded-md bg-background/95 shadow-xs hover:bg-background"
          aria-label={referencia.favorito ? "Remover dos favoritos" : "Favoritar"}
          aria-pressed={referencia.favorito}
          disabled={flags.isPending}
          onClick={aoFavoritar}
        >
          <Star
            className="size-4"
            aria-hidden="true"
            fill={referencia.favorito ? "currentColor" : "none"}
          />
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          className="size-8 rounded-md bg-background/95 shadow-xs hover:bg-background"
          aria-label="Excluir da biblioteca"
          disabled={flags.isPending}
          onClick={(evento) => {
            void aoExcluir(evento);
          }}
        >
          <Trash2 className="size-4" aria-hidden="true" />
        </Button>
      </div>
      <button
        type="button"
        onClick={aoAbrir}
        className="block w-full cursor-pointer rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <div className="relative aspect-square overflow-hidden bg-muted">
          {referencia.imageUrl || referencia.videoUrl ? (
            <MidiaAutenticada
              src={referencia.imageUrl ?? referencia.videoUrl}
              tipo={
                !referencia.imageUrl && referencia.videoUrl ? "video" : "image"
              }
              className="size-full object-cover"
            />
          ) : (
            <div className="flex size-full items-center justify-center text-muted-foreground">
              <Images className="size-7" aria-hidden="true" />
            </div>
          )}
          <div className="absolute right-2 top-2 flex max-w-[calc(100%-3.5rem)] flex-wrap items-center justify-end gap-1">
            {referencia.isAd ? (
              <Badge variant="secondary" className="shadow-xs">
                Anúncio
              </Badge>
            ) : null}
            {rotuloTipo ? (
              <span className="rounded-md bg-background/95 px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-xs">
                {rotuloTipo}
              </span>
            ) : null}
            {referencia.prescricao?.trim() ? (
              <Badge className="shadow-xs">Prescrição</Badge>
            ) : null}
          </div>
        </div>
        <div className="space-y-2 p-4">
          <p className="truncate text-sm font-medium tracking-tight text-foreground">
            {username}
          </p>
          <p className="line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {caption}
          </p>
          <div className="flex flex-col items-start gap-2 pt-1">
            <Badge
              variant="secondary"
              className="max-w-full truncate font-normal"
              title={referencia.collectionName}
            >
              {referencia.collectionName}
            </Badge>
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
              Ver detalhes
              <ExternalLink className="size-3.5" aria-hidden="true" />
            </span>
          </div>
        </div>
      </button>
    </article>
  );
}

function rotuloDoTipoDeMedia(tipo: Referencia["mediaType"]): string | null {
  switch (tipo) {
    case "image":
      return "Imagem";
    case "video":
      return "Vídeo";
    case "carousel":
      return "Carrossel";
    default:
      return null;
  }
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroDaApi) {
    if (erro.ehNaoAutorizado) {
      return "Token de acesso ausente ou inválido. Confira VITE_TOKEN_DA_API no .env do front.";
    }
    if (erro.ehProibido) {
      return "O token não tem o escopo necessário para esta consulta.";
    }
    return erro.message;
  }
  return erro instanceof Error ? erro.message : "Erro inesperado.";
}

function requestIdDoErro(erro: unknown): string | null {
  return erro instanceof ErroDaApi ? erro.requestId : null;
}
